import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as S from './styles'

import { getDefaultLanguage, changeClientLanguage } from '@/services/language'
import { EVENT_KEYS } from '@/services/events'
import { useTranslation } from 'react-i18next'

interface LanguageSwitcherProps {
  id?: string
  className?: string
}

const COUNTRY_BY_LANG = {
  pt: 'BR',
  es: 'ES',
  en: 'US'
} as const

const LANG_BY_COUNTRY = {
  BR: 'pt',
  ES: 'es',
  US: 'en'
} as const

const LANGUAGE_OPTIONS = [
  { country: 'BR', label: 'PT', fullLabel: 'Português' },
  { country: 'ES', label: 'ES', fullLabel: 'Español' },
  { country: 'US', label: 'EN', fullLabel: 'English' }
] as const

const LanguageSwitcher = ({
  id = 'inp_flagselect',
  className
}: LanguageSwitcherProps) => {
  const { i18n } = useTranslation()
  const [selectedFlag, setSelectedFlag] = useState<'BR' | 'ES' | 'US'>('BR')
  const [openDropdown, setOpenDropdown] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const defaultLanguage = getDefaultLanguage()
    changeClientLanguage(defaultLanguage, false)
    setSelectedFlag(
      COUNTRY_BY_LANG[defaultLanguage as 'pt' | 'es' | 'en'] || 'BR'
    )

    const handleLanguageChange = () => {
      const lang = i18n.language.split('-')[0] as 'pt' | 'es' | 'en'
      setSelectedFlag(COUNTRY_BY_LANG[lang] || 'BR')
    }

    document.addEventListener(EVENT_KEYS.languageChange, handleLanguageChange)
    return () => {
      document.removeEventListener(
        EVENT_KEYS.languageChange,
        handleLanguageChange
      )
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openDropdown) return

      const target = event.target as Node
      if (containerRef.current && !containerRef.current.contains(target)) {
        setOpenDropdown(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenDropdown(false)
      }
    }

    window.addEventListener('click', handleClickOutside)
    window.addEventListener('keydown', handleEscape)

    return () => {
      window.removeEventListener('click', handleClickOutside)
      window.removeEventListener('keydown', handleEscape)
    }
  }, [openDropdown])

  const selectedLanguage = useMemo(
    () =>
      LANGUAGE_OPTIONS.find(option => option.country === selectedFlag) ||
      LANGUAGE_OPTIONS[0],
    [selectedFlag]
  )

  const handleSelectLanguage = (country: 'BR' | 'ES' | 'US') => {
    setSelectedFlag(country)
    changeClientLanguage(LANG_BY_COUNTRY[country], false)
    setOpenDropdown(false)
  }

  return (
    <S.LanguageSwitcherWrapper className={className} ref={containerRef}>
      <button
        id={id}
        type="button"
        className="language-control"
        data-dropdown-toggle
        aria-expanded={openDropdown}
        aria-haspopup="true"
        aria-label="language-select"
        onClick={() => setOpenDropdown(prev => !prev)}
      >
        <span className="globe-icon" aria-hidden="true">
          <svg
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.0443 12.2432C13.1287 11.9386 12.1442 11.7111 11.1081 11.5638C11.2492 10.7352 11.3243 9.86693 11.3482 8.9781H15.1756C15.09 10.1835 14.685 11.2989 14.0443 12.2432ZM10.1419 14.9829C10.4603 14.2136 10.7209 13.3821 10.9212 12.5062C11.7809 12.6334 12.6052 12.8213 13.3859 13.068C12.5234 13.9874 11.4036 14.664 10.1419 14.9829ZM8.96423 15.1703C8.81028 15.1813 8.6568 15.1937 8.49998 15.1937C8.34315 15.1937 8.19015 15.1813 8.03572 15.1703C7.62884 14.3231 7.29941 13.3802 7.05556 12.3795C7.53082 12.3379 8.01229 12.3135 8.49998 12.3135C8.98766 12.3135 9.46914 12.3379 9.94487 12.3795C9.70055 13.3802 9.37112 14.3231 8.96423 15.1703ZM3.61402 13.068C4.3948 12.8213 5.21908 12.6334 6.07875 12.5062C6.27909 13.3821 6.53966 14.2136 6.85809 14.9829C5.59632 14.664 4.47703 13.9874 3.61402 13.068ZM2.95564 12.2432C2.31495 11.2989 1.90998 10.1835 1.82439 8.9781H5.65178C5.67569 9.86693 5.75076 10.7352 5.8918 11.5638C4.85571 11.7111 3.87125 11.9386 2.95564 12.2432ZM2.95564 4.75674C3.87125 5.06131 4.85571 5.28936 5.8918 5.43614C5.75076 6.26473 5.67569 7.13302 5.65178 8.02185H1.82439C1.90998 6.8165 2.31495 5.70104 2.95564 4.75674ZM6.85809 2.01707C6.53966 2.78637 6.27909 3.61783 6.07875 4.49375C5.21908 4.36705 4.3948 4.17867 3.61402 3.93196C4.47703 3.01253 5.59632 2.33598 6.85809 2.01707ZM8.03572 1.82966C8.19015 1.81914 8.34315 1.80623 8.49998 1.80623C8.6568 1.80623 8.81028 1.81914 8.96423 1.82966C9.37112 2.6769 9.70055 3.62023 9.94487 4.62046C9.46914 4.66206 8.98766 4.68644 8.49998 4.68644C8.01229 4.68644 7.53082 4.66206 7.05556 4.62046C7.29941 3.62023 7.62884 2.6769 8.03572 1.82966ZM10.3905 8.9781C10.3647 9.82008 10.2882 10.6482 10.1442 11.4433C9.60588 11.3965 9.05843 11.3687 8.49998 11.3687C7.94153 11.3687 7.39407 11.3965 6.85618 11.4433C6.71179 10.6482 6.63529 9.82008 6.60995 8.9781H10.3905ZM6.60995 8.02185C6.63529 7.17987 6.71179 6.35176 6.85618 5.55664C7.39407 5.60349 7.94153 5.63123 8.49998 5.63123C9.05843 5.63123 9.60588 5.60349 10.1438 5.55664C10.2882 6.35176 10.3647 7.17987 10.3905 8.02185H6.60995ZM13.3859 3.93196C12.6052 4.17867 11.7809 4.36705 10.9212 4.49375C10.7209 3.61783 10.4603 2.78637 10.1419 2.01707C11.4036 2.33598 12.5234 3.01253 13.3859 3.93196ZM14.0443 4.75674C14.685 5.70104 15.09 6.8165 15.1756 8.02185H11.3482C11.3243 7.13302 11.2492 6.26473 11.1081 5.43614C12.1442 5.28936 13.1287 5.06131 14.0443 4.75674ZM8.49998 0.849976C4.27526 0.849976 0.849976 4.27479 0.849976 8.49998C0.849976 12.7252 4.27526 16.15 8.49998 16.15C12.7247 16.15 16.15 12.7252 16.15 8.49998C16.15 4.27479 12.7247 0.849976 8.49998 0.849976Z"
              fill="#7E7E7E"
            />
          </svg>
        </span>
        <span className="language-current">{selectedLanguage.label}</span>
      </button>

      {openDropdown && (
        <div
          className="dropdown-menu"
          role="menu"
          aria-label="language-select-menu"
        >
          <div className="dropdown-column">
            {LANGUAGE_OPTIONS.map(option => (
              <button
                key={option.country}
                type="button"
                role="menuitem"
                lang={LANG_BY_COUNTRY[option.country]}
                className={selectedFlag === option.country ? 'active' : ''}
                onClick={() => handleSelectLanguage(option.country)}
              >
                {option.fullLabel}
              </button>
            ))}
          </div>
        </div>
      )}
    </S.LanguageSwitcherWrapper>
  )
}

export default LanguageSwitcher
